import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { ThemeProvider, StyleSheetManager } from 'styled-components';

/**
 * Защита от styled-components warning #12 («unknown prop sent to DOM»):
 * SC v6 по умолчанию форвардит все props в DOM-элемент. Если стилевой prop
 * не помечен через `$`-префикс, он попадает в HTML и SC ругается в консоли.
 * В нашем коде стилевые props следуют конвенции `$active`, `$tone` и т.п. —
 * блокируем форвард любого prop, начинающегося с `$`. Стандартные DOM-атрибуты
 * (className, onClick, role, aria-*, data-*) форвардятся без изменений.
 */
function shouldForwardProp(prop: string): boolean {
  return !prop.startsWith('$');
}
import { BrowserRouter, Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { store } from './store';
import { useAppSelector } from './hooks';
import { realtime } from '@shared/realtime/ws';
import { installRealtimeBridge } from '@shared/realtime/rtkBridge';
import { themesByName } from '@shared/theme/tokens';
import { GlobalStyle } from '@shared/theme/GlobalStyle';
import { useMeQuery } from '@shared/api';
import { useProactiveTokenRefresh } from '@features/auth/model/useProactiveTokenRefresh';
import { useThemeSync } from '@features/theme/model/useThemeSync';
import { LoginPage } from '@pages/Login/LoginPage';
import { DashboardPage } from '@pages/Dashboard/DashboardPage';
import { StatutLayout } from '@pages/Statut/StatutLayout';
import { StatutDashboard } from '@pages/Statut/StatutDashboard';
import { ComplianceCheck } from '@pages/Statut/ComplianceCheck';
import { KnowledgeBase } from '@pages/Statut/KnowledgeBase';
import { TrashBin } from '@pages/Statut/TrashBin';
import { ChatTab } from '@pages/Statut/ChatTab';
import { Contracts } from '@pages/Statut/Contracts';
import { Counterparties } from '@pages/Statut/Counterparties';
import { Metrics } from '@pages/Statut/Metrics';
import { Claims } from '@pages/Statut/Claims';
import { Correspondence } from '@pages/Statut/Correspondence';
import { Deadlines } from '@pages/Statut/Deadlines';
import { PenaltyCalculator } from '@pages/Statut/PenaltyCalculator';
import { Gov } from '@pages/Statut/Gov';
import { Monitoring } from '@pages/Statut/Monitoring';
import { Lna } from '@pages/Statut/Lna';
import { ComposedDocs } from '@pages/Statut/ComposedDocs';
import { DocumentPage } from '@pages/Statut/DocumentPage';
import { ClaimPage } from '@pages/Statut/ClaimPage';
import { LawsuitPage } from '@pages/Statut/LawsuitPage';
import { ContractPage } from '@pages/Statut/ContractPage';
import { ContractBriefView } from '@pages/Statut/ContractBriefView';
import { CounterpartyPage } from '@pages/Statut/CounterpartyPage';
import { DispatcherPage } from '@pages/Dispatcher/DispatcherPage';
import { SmetaLayout } from '@pages/Smeta/SmetaLayout';
import { SmetaPage } from '@pages/Smeta/SmetaPage';
import { VolumeChecks } from '@pages/Smeta/VolumeChecks';
import { WriteOffChecks } from '@pages/Smeta/WriteOffChecks';
import { ZviChecks } from '@pages/Smeta/ZviChecks';
import { KsChecks } from '@pages/Smeta/KsChecks';
import { KsPackages } from '@pages/Smeta/KsPackages';
import { TenderEstimates } from '@pages/Smeta/TenderEstimates';
import { GsChecks } from '@pages/Smeta/GsChecks';
import { SmetaChat } from '@pages/Smeta/SmetaChat';
import { Registers } from '@pages/Smeta/Registers';
import { Accumulations } from '@pages/Smeta/Accumulations';
import { MaterialNorms } from '@pages/Smeta/MaterialNorms';
import { VatRates } from '@pages/Smeta/VatRates';
import { ContractLsr } from '@pages/Smeta/ContractLsr';
import { FgiscsPeriods } from '@pages/Smeta/FgiscsPeriods';
import { NormBase } from '@pages/Smeta/NormBase';
import { RateChecks } from '@pages/Smeta/RateChecks';
import { RateCheckDetail } from '@pages/Smeta/RateCheckDetail';
import { RateCheckDiscrepancies } from '@pages/Smeta/RateCheckDiscrepancies';
import { EconLayout } from '@pages/Economist/EconLayout';
import { EconPage } from '@pages/Economist/EconPage';
import { RunsList } from '@pages/Economist/RunsList';
import { RunDetail } from '@pages/Economist/RunDetail';
import { TemplatesView } from '@pages/Economist/TemplatesView';
import { HandbooksView } from '@pages/Economist/HandbooksView';
// Хронум Экономиста: снимки, свод часов месяца, должности и площадки.
import { ChronumView } from '@pages/Economist/chronum';
import { FleetView } from '@pages/Economist/FleetView';
import { PlanVersionsView } from '@pages/Economist/PlanVersionsView';
// Группы и ставки: средние ставки маш.-часа групп механизаторов по объектам.
import { GroupRatesView } from '@pages/Economist/GroupRatesView';
// ПРЦ машино-часа Экономиста: версии, карточка версии, печатная форма по машине.
import { PrcPrintPage, PrcVersionPage, PrcVersionsPage } from '@pages/Economist/prc';
import { PtoLayout } from '@pages/Pto/PtoLayout';
import { PtoProjects } from '@pages/Pto/PtoProjects';
import { PtoProjectView } from '@pages/Pto/PtoProjectView';
import { PtoRdImportHome, PtoRdImportView } from '@pages/Pto/rdImport';
import { PtoVolumes } from '@pages/Pto/PtoVolumes';
import { PtoRdSheetWindow } from '@pages/Pto/rdSheet/PtoRdSheetWindow';
import { PtoReports } from '@pages/Pto/PtoReports';
import { PtoActs } from '@pages/Pto/PtoActs';
import { PtoOzhr } from '@pages/Pto/PtoOzhr';
import { PtoKs } from '@pages/Pto/PtoKs';
import { PtoSmrRegisters } from '@pages/Pto/PtoSmrRegisters';
import { PtoKfo } from '@pages/Pto/PtoKfo';
import { PtoReconciliation } from '@pages/Pto/PtoReconciliation';
import { PtoRequests } from '@pages/Pto/PtoRequests';
import { PtoChanges } from '@pages/Pto/PtoChanges';
import { PtoInspections } from '@pages/Pto/PtoInspections';
import { PtoChecklist } from '@pages/Pto/PtoChecklist';
import { PtoPprTkLibrary } from '@pages/Pto/PtoPprTkLibrary';
import { PtoChat } from '@pages/Pto/PtoChat';
import { PtoExecSchemes, PtoExecSchemeCard } from '@pages/Pto/execSchemes';
import { GeodesyLayout } from '@pages/Geodesy/GeodesyLayout';
import { GeodesySchemes } from '@pages/Geodesy/GeodesySchemes';
import { GeodesyRequests } from '@pages/Geodesy/GeodesyRequests';
import { GeoSchemeCard } from '@pages/Geodesy/scheme/GeoSchemeCard';
import { MtoLayout } from '@pages/Mto/MtoLayout';
import { KadryLayout } from '@pages/Kadry/KadryLayout';
import { KadryHome } from '@pages/Kadry/KadryHome';
import { KadryGuard } from '@pages/Kadry/ui';
import { RequestsList as KadryRequestsList } from '@pages/Kadry/Requests/RequestsList';
import { RequestCard as KadryRequestCard } from '@pages/Kadry/Requests/RequestCard';
import { SettingsLayout as KadrySettingsLayout } from '@pages/Kadry/Settings/SettingsLayout';
import { WorkSites as KadryWorkSites } from '@pages/Kadry/Settings/WorkSites';
import { Autooffer as KadryAutooffer } from '@pages/Kadry/Settings/Autooffer';
import { FeatureGates as KadryFeatureGates } from '@pages/Kadry/Settings/FeatureGates';
import { Connections as KadryConnections } from '@pages/Kadry/Settings/Connections';
import { KadryAudit } from '@pages/Kadry/Audit';
import { KadryNotifications } from '@pages/Kadry/Notifications';
import { KadryInterested } from '@pages/Kadry/Interested';
import { KADRY_REQUESTS_OPS } from '@entities/kadry';
import { MtoDashboard } from '@pages/Mto/MtoDashboard';
import { LogistPage } from '@pages/Logist/LogistPage';
import { LogistDictionaries } from '@pages/Logist/LogistDictionaries';
import { LogistSigners } from '@pages/Logist/LogistSigners';
import { LogistOrders } from '@pages/Logist/LogistOrders';
import { LogistEdoBoxes } from '@pages/Logist/LogistEdoBoxes';
import { LogistTasks } from '@pages/Logist/LogistTasks';
import { LogistTrips } from '@pages/Logist/LogistTrips';
import { LogistRegistry } from '@pages/Logist/LogistRegistry';
import { MeetingsLayout } from '@pages/Meetings/MeetingsLayout';
import { MeetingsDashboard } from '@pages/Meetings/MeetingsDashboard';
import { MeetingsDepartments } from '@pages/Meetings/MeetingsDepartments';
import { MeetingsEmployees } from '@pages/Meetings/MeetingsEmployees';
import { MeetingDepartment } from '@pages/Meetings/MeetingDepartment';
import { MeetingRegister } from '@pages/Meetings/MeetingRegister';
import { MeetingsArchive } from '@pages/Meetings/MeetingsArchive';
import { MeetingDetail } from '@pages/Meetings/MeetingDetail';
import { FieldLayout } from '@pages/Field/FieldLayout';
import { KspPage } from '@pages/Ksp/KspPage';
import { FieldDashboard } from '@pages/Field/FieldDashboard';
import { FieldSiteDetail } from '@pages/Field/FieldSiteDetail';
import { FieldReportDetail } from '@pages/Field/FieldReportDetail';
import { FieldFactDetail } from '@pages/Field/FieldFactDetail';
import { RequestsPage } from '@pages/Requests/RequestsPage';
import { RequestDetail } from '@pages/Requests/RequestDetail';
import { MtoRunsList } from '@pages/Mto/MtoRunsList';
import { MtoRunDetail } from '@pages/Mto/MtoRunDetail';
import { MtoQuality } from '@pages/Mto/MtoQuality';
import { MtoRequests } from '@pages/Mto/MtoRequests';
import { MtoSuppliers } from '@pages/Mto/MtoSuppliers';
import { ReceiptsPage } from '@pages/Mto/receipts';
import { BdrPage } from '@pages/Economist/bdr';
import { ProgressLayout } from '@pages/Progress/ProgressLayout';
import { ProgressObjectDashboard } from '@pages/Progress/ProgressObjectDashboard';
import { ProgressReports } from '@pages/Progress/ProgressReports';
import { ProgressInstructions } from '@pages/Progress/ProgressInstructions';
import { ExpertPage } from '@pages/Expert/ExpertPage';
import { MyInstructionsPage } from '@pages/Instructions/MyInstructionsPage';
import { PayRequestsPage } from '@pages/PayRequests/PayRequestsPage';
import { PayRequestCard } from '@pages/PayRequests/PayRequestCard';
import { ProgressRunsList } from '@pages/Progress/ProgressRunsList';
import { ProgressRunDetail } from '@pages/Progress/ProgressRunDetail';
import { CeoLayout } from '@pages/Ceo/CeoLayout';
import { CeoDashboardPage } from '@pages/Ceo/CeoDashboardPage';
import { CeoBankAccountsPage } from '@pages/Ceo/CeoBankAccountsPage';
import { CeoDebtsPage } from '@pages/Ceo/CeoDebtsPage';
import { CeoInflowsPage } from '@pages/Ceo/CeoInflowsPage';
import { CeoApprovalsPage } from '@pages/Ceo/CeoApprovalsPage';
import { FinancierLayout } from '@pages/Financier/FinancierLayout';
import { FinancierDashboardPage } from '@pages/Financier/FinancierDashboardPage';
import { FinancierRequestsPage } from '@pages/Financier/FinancierRequestsPage';
import { FinancierCounterpartiesPage } from '@pages/Financier/FinancierCounterpartiesPage';
import { FinancierChronumPayrollPage } from '@pages/Financier/FinancierChronumPayrollPage';
import { FinancierWorkingCapitalPage } from '@pages/Financier/FinancierWorkingCapitalPage';
import { SecurityPage } from '@pages/Security/SecurityPage';
import { AssistantPage } from '@pages/Assistant/AssistantPage';
import { AdminLayout } from '@pages/Admin/AdminLayout';
import { AdminDashboardTab } from '@pages/Admin/AdminDashboardTab';
import { AdminServicesTab } from '@pages/Admin/AdminServicesTab';
import { AdminUsersTab } from '@pages/Admin/AdminUsersTab';
import { AdminSystemTab } from '@pages/Admin/AdminSystemTab';
import { AdminAnnouncementsTab } from '@pages/Admin/AdminAnnouncementsTab';
import { AdminAuditTab } from '@pages/Admin/AdminAuditTab';
import { AdminAssistantTab } from '@pages/Admin/AdminAssistantTab';
import { AppShell, useMfaEnforcement } from '@widgets/AppShell/AppShell';
import { DiskFallback } from '@pages/Disk/DiskFallback';

// ОСНОВА.Диск — первое разбиение кода в проекте: окно Диска со своими ручками,
// очередью загрузок и просмотрщиками — отдельный чанк, остальная ОСНОВА его
// не грузит, пока человек не открыл /disk.
const DiskPage = lazy(() => import('@pages/Disk'));

function ThemedApp() {
  const themeName = useAppSelector((s) => s.theme.current);
  // Тема меняется во всех окнах ОСНОВЫ сразу (окно Диска, лист РД).
  useThemeSync();
  const theme = themesByName[themeName];
  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        {/*
          Пропа `future` в react-router 7 больше нет: флаги v7_startTransition и
          v7_relativeSplatPath были опт-ином в шестёрке и стали поведением по
          умолчанию. Мы их включали заранее — поэтому переход на 7 не меняет
          навигацию, а просто снимает подготовительную обвязку.
        */}
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Страницы без оболочки ОСНОВЫ (меню и шапки): ОСНОВА.Эксперт — чат
                руководителя во весь экран в духе ChatGPT, со своей кнопкой
                «Вернуться в ОСНОВУ». expert/:sessionId — открытый чат. Один
                маршрут на оба адреса: при переходе нового чата на свой адрес
                страница не пересоздаётся и печатающийся ответ не обрывается. */}
            <Route element={<ProtectedStandalone />}>
              <Route path="expert/:sessionId?" element={<ExpertPage />} />
              {/* ПРЦ: печатный вид «Калькуляции (форма)» по машине — без меню и
                шапки ОСНОВЫ, на печать идёт только лист (A4 альбомная). */}
              <Route path="agents/econ/prc/:id/print" element={<PrcPrintPage />} />
              {/* Окно исходного листа РД (ОСНОВА.Инженер, пункт 9 плана ПТО) —
                отдельное окно браузера для второго монитора. */}
              <Route path="agents/pto/rd-sheet" element={<PtoRdSheetWindow />} />
              {/* ОСНОВА.Диск — файловое хранилище отделов во весь экран, со
                своей шапкой и «Вернуться в ОСНОВУ». Адреса: /disk, /disk/n/:id,
                /disk/search?q=, /disk/trash|recent|favorites|shared. */}
              <Route
                path="disk/*"
                element={
                  <Suspense fallback={<DiskFallback />}>
                    <DiskPage />
                  </Suspense>
                }
              />
            </Route>
            <Route element={<ProtectedShell />}>
              <Route index element={<DashboardPage />} />
              <Route path="security" element={<SecurityPage />} />
              <Route path="assistant" element={<AssistantPage />} />
              {/* «Мои поручения» — поручения Руководителя исполнителю: любому
                сотруднику, без прав на агента (видны только свои). */}
              <Route path="instructions" element={<MyInstructionsPage />} />
              {/* «Заявки на оплату» — счёт от сотрудника любой службы сразу в Фабрику
                платежей Финансиста. Без прав на агента: что кому видно, решает сервер. */}
              <Route path="pay-requests" element={<PayRequestsPage />} />
              <Route path="pay-requests/:id" element={<PayRequestCard />} />
              {/* Безопасный паспорт договора для смежных служб (без доступа к Юристу). */}
              <Route path="contracts/:id/brief" element={<ContractBriefView />} />
              <Route path="agents/statut" element={<StatutLayout />}>
                <Route index element={<StatutDashboard />} />
                <Route path="contracts" element={<Contracts kind="contract" />} />
                <Route path="contracts/:id" element={<ContractPage />} />
                {/* Преддоговорная работа — незаключённые договоры. Тот же
                  компонент реестра с kind='precontract'; карточка общая
                  (ContractPage по id). */}
                <Route path="precontracts" element={<Contracts kind="precontract" />} />
                <Route path="precontracts/:id" element={<ContractPage />} />
                {/* Типовые формы слиты в Базу знаний (фильтр source=template).
                  Старый URL редиректим для обратной совместимости. */}
                <Route
                  path="contract-templates"
                  element={<Navigate to="/agents/statut/knowledge?source=template" replace />}
                />
                <Route path="counterparties" element={<Counterparties />} />
                <Route path="counterparties/:id" element={<CounterpartyPage />} />
                <Route path="metrics" element={<Metrics />} />
                <Route path="claims" element={<Claims />} />
                <Route path="claims/:id" element={<ClaimPage />} />
                <Route path="lawsuits/:id" element={<LawsuitPage />} />
                <Route path="correspondence" element={<Correspondence />} />
                <Route path="deadlines" element={<Deadlines />} />
                <Route path="calculator" element={<PenaltyCalculator />} />
                <Route path="compliance" element={<ComplianceCheck />} />
                <Route path="knowledge" element={<KnowledgeBase />} />
                <Route path="chat" element={<ChatTab />} />
                <Route path="gov" element={<Gov />} />
                <Route path="monitoring" element={<Monitoring />} />
                <Route path="lna" element={<Lna />} />
                <Route path="documents" element={<ComposedDocs />} />
                <Route path="documents/:composedId" element={<DocumentPage />} />
                <Route path="trash" element={<TrashBin />} />
              </Route>
              {/* Агент-диспетчер: отдельный раздел рядом с юристом. */}
              <Route path="agents/dispatcher" element={<DispatcherPage />} />
              {/* Агент-сметчик (отдел СЦО). */}
              <Route path="agents/smeta" element={<SmetaLayout />}>
                <Route index element={<SmetaPage />} />
                <Route path="volume-checks" element={<VolumeChecks />} />
                <Route path="write-off-checks" element={<WriteOffChecks />} />
                <Route path="ks-checks" element={<KsChecks />} />
                {/* Пакет акта: не проверка загруженного КС-2, а СБОРКА комплекта
                    КС-2/КС-3 из фактов. Страница сама показывает баннер, когда
                    OSNOVA_KS_PACKAGE_ENABLED выключен (роуты не зарегистрированы). */}
                <Route path="ks-packages" element={<KsPackages />} />
                <Route path="tender-estimates" element={<TenderEstimates />} />
                <Route path="gs-checks" element={<GsChecks />} />
                <Route path="zvi-checks" element={<ZviChecks />} />
                <Route path="chat" element={<SmetaChat />} />
                <Route path="registers" element={<Registers />} />
                <Route path="accumulations" element={<Accumulations />} />
                <Route path="fgiscs-periods" element={<FgiscsPeriods />} />
                {/* Нормативная база ФСНБ-2022: поиск, карточки, редакции
                    (загрузка дополнения с ФГИС ЦС и ввод в действие). Вкладка
                    и раскрытая редакция — в ?tab=editions&edition=<id>. */}
                <Route path="norm-base" element={<NormBase />} />
                <Route path="material-norms" element={<MaterialNorms />} />
                <Route path="vat-rates" element={<VatRates />} />
                <Route path="contract-lsr" element={<ContractLsr />} />
                <Route path="rate-checks" element={<RateChecks />} />
                <Route path="rate-checks/:id" element={<RateCheckDetail />} />
                <Route path="rate-checks/:id/discrepancies" element={<RateCheckDiscrepancies />} />
              </Route>
              {/* Агент-экономист (планово-экономический отдел): управленческая
                отчётность (БДР, добыча ПИ, столовая). EconLayout даёт шапку и
                <Outlet/>; index — витрина модулей + сводка, runs — прогоны
                (модуль в ?module=), runs/:id — карточка прогона с трассировкой
                фактов и HITL-разметкой аномалий. */}
              <Route path="agents/econ" element={<EconLayout />}>
                <Route index element={<EconPage />} />
                <Route path="runs" element={<RunsList />} />
                <Route path="runs/:id" element={<RunDetail />} />
                <Route path="templates" element={<TemplatesView />} />
                <Route path="handbooks" element={<HandbooksView />} />
                {/* Хронум (ТЗ v2.1 п. 6.4, 6.8, 7.4): вкладка, месяц и фильтры — в
                  ?tab=hours|positions|sites|history&period=ГГГГ-ММ&filter=&site=. */}
                <Route path="chronum" element={<ChronumView />} />
                {/* Справочник техники платформы (ТЗ v2.1 п. 6.1): карточка модели
                  открывается в боковой панели по ?model=<id>. */}
                <Route path="fleet" element={<FleetView />} />
                {/* ПРЦ машино-часа (ТЗ v2.1 п. 6.2): список версий и версия;
                  вкладка, год и модель — в ?tab=&year=&model=. */}
                <Route path="prc" element={<PrcVersionsPage />} />
                <Route path="prc/:id" element={<PrcVersionPage />} />
                <Route path="plan-versions" element={<PlanVersionsView />} />
                {/* Группы и ставки (ТЗ v2.1 п. 6.3): объект и дата — в ?object=, ?on=,
                  панель группы — ?group=<id>&view=new|history. */}
                <Route path="group-rates" element={<GroupRatesView />} />
                {/* БДР v2: месяц × объект из первичных источников (Э6); ?month=&scope=. */}
                <Route path="bdr" element={<BdrPage />} />
                {/* Приход ТМЦ отдела МТО — тот же экран: материалы БДР по объектам. */}
                <Route path="receipts" element={<ReceiptsPage context="econ" />} />
              </Route>
              {/* Агент ОСНОВА.Инженер (ИИ-агент ПТО): реестр РД с версионностью
                и чат-с-РД (M1). PtoLayout даёт шапку и <Outlet/>; index —
                витрина проектов, :projectId — реестр документов и ревизий,
                :projectId/chat — чат-с-РД с обязательными ссылками. */}
              <Route path="agents/pto" element={<PtoLayout />}>
                <Route index element={<PtoProjects />} />
                {/* Загрузка РД архивом и очередь обработки РД — статический путь
                  перебивает :projectId. */}
                <Route path="rd-import" element={<PtoRdImportHome />} />
                <Route path="rd-import/:importId" element={<PtoRdImportView />} />
                {/* Исполнительные схемы от ОСНОВА.Геодезист (AI-GEO-2026 §6): проверка,
                  передача заказчику, обратная связь, «запросить у геодезии». */}
                <Route path="exec-schemes" element={<PtoExecSchemes />} />
                <Route path="exec-schemes/:id" element={<PtoExecSchemeCard />} />
                <Route path=":projectId" element={<PtoProjectView />} />
                <Route path=":projectId/volumes/:revisionId" element={<PtoVolumes />} />
                <Route path=":projectId/reports" element={<PtoReports />} />
                <Route path=":projectId/reconciliation" element={<PtoReconciliation />} />
                <Route path=":projectId/requests" element={<PtoRequests />} />
                <Route path=":projectId/changes" element={<PtoChanges />} />
                <Route path=":projectId/inspections" element={<PtoInspections />} />
                <Route path=":projectId/checklists" element={<PtoChecklist />} />
                <Route path=":projectId/ppr-tk" element={<PtoPprTkLibrary />} />
                <Route path=":projectId/acts" element={<PtoActs />} />
                <Route path=":projectId/ozhr" element={<PtoOzhr />} />
                <Route path=":projectId/ks" element={<PtoKs />} />
                <Route path=":projectId/smr-registers" element={<PtoSmrRegisters />} />
                <Route path=":projectId/kfo" element={<PtoKfo />} />
                <Route path=":projectId/chat" element={<PtoChat />} />
              </Route>
              {/* Агент ОСНОВА.МТО (материально-техническое обеспечение).
                MtoLayout — постоянный левый рельс модулей + <Outlet/>; index —
                обзор (сводка + справочник НСИ); runs — рабочий раздел модуля
                (модуль в `?module=`): форма запуска обработки + её обработки, без
                модуля — сводный реестр всех обработок; runs/:id — карточка
                обработки с результатами, аномалиями и HITL-разметкой. */}
              {/* ОСНОВА.Кадры (ТЗ v1.1, ADR-0019): оболочка + разделы по мере готовности.
                Разделы охраняются KadryGuard по операциям из /info (Прил. Б): без
                нужной операции или при выключенном модуле — объяснение вместо 403. */}
              <Route path="agents/kadry" element={<KadryLayout />}>
                <Route index element={<KadryHome />} />
                <Route element={<KadryGuard anyOf={KADRY_REQUESTS_OPS} />}>
                  <Route path="requests" element={<KadryRequestsList />} />
                  <Route path="requests/:id" element={<KadryRequestCard />} />
                </Route>
                <Route
                  path="settings"
                  element={
                    <KadryGuard anyOf={['admin_settings']}>
                      <KadrySettingsLayout />
                    </KadryGuard>
                  }
                >
                  <Route index element={<Navigate to="connections" replace />} />
                  <Route path="connections" element={<KadryConnections />} />
                  <Route path="sites" element={<KadryWorkSites />} />
                  <Route path="autooffer" element={<KadryAutooffer />} />
                  <Route path="gates" element={<KadryFeatureGates />} />
                </Route>
                <Route element={<KadryGuard anyOf={['audit_log_view']} />}>
                  <Route path="audit" element={<KadryAudit />} />
                </Route>
                <Route path="notifications" element={<KadryNotifications />} />
                <Route path="interested" element={<KadryInterested />} />
              </Route>
              <Route path="agents/mto" element={<MtoLayout />}>
                <Route index element={<MtoDashboard />} />
                <Route path="runs" element={<MtoRunsList />} />
                <Route path="runs/:id" element={<MtoRunDetail />} />
                {/* Подфункция «Документы качества для АОСР» (AI-MTO-2026-QC):
                  реестр партий, документы качества, применение, действительность
                  (МТОК-4) и дефицит (МТОК-6). Вне runs-логики (свой роут). */}
                <Route path="quality" element={<MtoQuality />} />
                {/* Входящие заявки в снабжение от ПТО и прорабов (тип mto_supply
                  сквозных заявок): приём, возврат, работа, ответ. Карточка —
                  общая, /requests/:id. */}
                <Route path="requests" element={<MtoRequests />} />
                {/* Реестр поставщиков (2.3): загрузка файлом, ИНН, контакты. */}
                <Route path="suppliers" element={<MtoSuppliers />} />
                {/* Приход ТМЦ: журнал поступлений материалов по объектам (ведёт
                  МТО, материалы БДР Экономист берёт отсюда). ?tab=&year=&month=. */}
                <Route path="receipts" element={<ReceiptsPage context="mto" />} />
              </Route>
              {/* Агент ОСНОВА.Геодезист (ТЗ AI-GEO-2026): индекс — реестр
                исполнительных схем; requests — входящие заявки на съёмку;
                schemes/:id — мастер ИС из 9 шагов регламента камеральщика. */}
              <Route path="agents/geodesy" element={<GeodesyLayout />}>
                <Route index element={<GeodesySchemes />} />
                <Route path="requests" element={<GeodesyRequests />} />
                <Route path="schemes/:id" element={<GeoSchemeCard />} />
              </Route>
              {/* Агент ОСНОВА.Руководитель. ProgressLayout — hero + вкладки
                (Дашборд объекта / Поручения / Сводные отчёты) и <Outlet/>; index —
                дашборд объекта (выбор объекта → переписка, претензии, прогресс
                КФО, процентовка, КСП); instructions — реестр поручений по
                показателям; advisor — старый адрес ОСНОВА.Эксперта, теперь
                перекидывает на отдельную страницу /expert; reports — форма запуска сводного отчёта;
                runs — реестр отчётов; runs/:id — карточка отчёта с KPI,
                пояснительной запиской, скачиванием DOCX и HITL-решением. */}
              <Route path="agents/progress" element={<ProgressLayout />}>
                <Route index element={<ProgressObjectDashboard />} />
                <Route path="instructions" element={<ProgressInstructions />} />
                <Route path="advisor" element={<LegacyAdvisorRedirect />} />
                <Route path="advisor/:sessionId" element={<LegacyAdvisorRedirect />} />
                <Route path="reports" element={<ProgressReports />} />
                <Route path="runs" element={<ProgressRunsList />} />
                <Route path="runs/:id" element={<ProgressRunDetail />} />
              </Route>
              <Route path="agents/ceo" element={<CeoLayout />}>
                <Route index element={<CeoDashboardPage />} />
                <Route path="accounts" element={<CeoBankAccountsPage />} />
                <Route path="debts" element={<CeoDebtsPage />} />
                <Route path="inflows" element={<CeoInflowsPage />} />
                <Route path="approvals" element={<CeoApprovalsPage />} />
              </Route>
              <Route path="agents/finance" element={<FinancierLayout />}>
                <Route index element={<FinancierDashboardPage />} />
                <Route path="requests" element={<FinancierRequestsPage />} />
                {/* Счета, кредиты и планы поступлений ведёт Финансист; в Пульте
                    Гендиректора те же экраны — только для просмотра */}
                <Route path="accounts" element={<CeoBankAccountsPage editable />} />
                <Route path="debts" element={<CeoDebtsPage editable />} />
                <Route path="inflows" element={<CeoInflowsPage editable />} />
                <Route path="counterparties" element={<FinancierCounterpartiesPage />} />
                <Route path="chronum-payroll" element={<FinancierChronumPayrollPage />} />
                <Route path="working-capital" element={<FinancierWorkingCapitalPage />} />
              </Route>
              <Route path="agents/ksp" element={<KspPage />} />
              <Route path="agents/ksp/projects/:projectId" element={<KspPage />} />
              {/* ОСНОВА.Совещания — автопротоколирование (каркас витрины; бэкенд
                за OSNOVA_MEETINGS_ENABLED). Обзор = карточка агента + модули. */}
              {/* ОСНОВА.Прораб — полевой сбор данных. FieldLayout даёт шапку и
                <Outlet/>; index — обзор (модули агента + список площадок).
                Ввод данных — в мобильном приложении; веб — обзор + приёмка
                рапортов ПТО (СР-5). */}
              <Route path="agents/field" element={<FieldLayout />}>
                <Route index element={<FieldDashboard />} />
                <Route path=":siteId" element={<FieldSiteDetail />} />
                <Route path=":siteId/reports/:reportId" element={<FieldReportDetail />} />
                <Route path=":siteId/volume-facts/:factId" element={<FieldFactDetail />} />
                {/* Без площадки в пути — ссылки уведомлений с голым id рапорта/факта:
                    площадку страница берёт из самого рапорта/факта. */}
                <Route path="reports/:reportId" element={<FieldReportDetail />} />
                <Route path="volume-facts/:factId" element={<FieldFactDetail />} />
              </Route>
              <Route path="agents/meetings" element={<MeetingsLayout />}>
                <Route index element={<MeetingsDashboard />} />
                <Route path="register" element={<MeetingRegister />} />
                <Route path="archive" element={<MeetingsArchive />} />
                <Route path="departments" element={<MeetingsDepartments />} />
                <Route path="employees" element={<MeetingsEmployees />} />
                <Route path="department/:departmentId" element={<MeetingDepartment />} />
                <Route path="meetings/:id" element={<MeetingDetail />} />
              </Route>
              {/* Агент ОСНОВА.Логист (ЭТрН, ТЗ AI-LOGIST-2026): каркас-витрина
                модуля по волнам. Функциональный контур (справочники, заказы,
                серии рейсов, весовой контроль, реестр ЭТрН) — отдельными PR за
                фичефлагом OSNOVA_LOGIST_ENABLED. */}
              <Route path="agents/logist" element={<LogistPage />} />
              <Route path="agents/logist/dictionaries" element={<LogistDictionaries />} />
              <Route path="agents/logist/signers" element={<LogistSigners />} />
              <Route path="agents/logist/orders" element={<LogistOrders />} />
              <Route path="agents/logist/edo-boxes" element={<LogistEdoBoxes />} />
              <Route path="agents/logist/tasks" element={<LogistTasks />} />
              <Route path="agents/logist/trips" element={<LogistTrips />} />
              <Route path="agents/logist/registry" element={<LogistRegistry />} />
              {/* Сквозные заявки между функциями (requests-core, ADR-0016) —
                не под конкретным агентом; RBAC по функции source/target. */}
              <Route path="requests" element={<RequestsPage />} />
              <Route path="requests/:id" element={<RequestDetail />} />
              {/* admin-секция, защищена AdminGuard (только роль 'admin') */}
              <Route
                path="admin"
                element={
                  <AdminGuard>
                    <AdminLayout />
                  </AdminGuard>
                }
              >
                <Route index element={<AdminDashboardTab />} />
                <Route path="users" element={<AdminUsersTab />} />
                <Route path="services" element={<AdminServicesTab />} />
                <Route path="system" element={<AdminSystemTab />} />
                <Route path="announcements" element={<AdminAnnouncementsTab />} />
                <Route path="assistant" element={<AdminAssistantTab />} />
                <Route path="audit" element={<AdminAuditTab />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </StyleSheetManager>
  );
}

function ProtectedShell() {
  const authed = useAppSelector((s) => !!s.auth.accessToken);
  // Proactive refresh: тикает таймер на exp-60s, юзер не видит 401.
  // Хук безопасно вызывается даже если authed=false — внутри он
  // ничего не делает без accessToken.
  useProactiveTokenRefresh();
  if (!authed) return <Navigate to="/login" replace />;
  return (
    <>
      <RealtimeConnector />
      <AppShell />
    </>
  );
}

/**
 * Защищённые страницы без оболочки ОСНОВЫ — сейчас только ОСНОВА.Эксперт. Тот
 * же вход, обновление токена, живое соединение и кик обязательной 2FA, что у
 * ProtectedShell, но без меню и шапки: интерфейс у страницы свой.
 */
function ProtectedStandalone() {
  const authed = useAppSelector((s) => !!s.auth.accessToken);
  useProactiveTokenRefresh();
  if (!authed) return <Navigate to="/login" replace />;
  return (
    <>
      <RealtimeConnector />
      <StandaloneFrame />
    </>
  );
}

/** Рендерится только после входа: иначе /auth/me ушёл бы без токена. */
function StandaloneFrame() {
  useMfaEnforcement();
  return <Outlet />;
}

/** Старый адрес Эксперта (вкладка Руководителя) → отдельная страница; назад —
 *  к Руководителю. Ссылки из закладок и старых сообщений не ломаются. */
function LegacyAdvisorRedirect() {
  const { sessionId } = useParams<{ sessionId?: string }>();
  return (
    <Navigate
      to={sessionId ? `/expert/${sessionId}` : '/expert'}
      replace
      state={{ from: '/agents/progress' }}
    />
  );
}

/**
 * Защитник /admin/* роутов. Пока загружается /auth/me — рендерит ничего
 * (быстрая операция, RTKQ закэширует). Если у юзера нет роли 'admin' —
 * редирект на главную. Backend дублирует эту защиту через `require_admin`,
 * это просто чтобы не мигало UI которого юзер не должен видеть.
 */
function AdminGuard({ children }: { children: ReactNode }) {
  const { data: me, isLoading } = useMeQuery();
  if (isLoading) return null;
  if (!me?.roles?.includes('admin')) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * Держит WebSocket-соединение пока юрист залогинен. При смене токена
 * (refresh / logout) автоматически переподключается или закрывается.
 * Bridge подписывает RTK Query на события — компоненты будут обновляться
 * только когда что-то реально произошло на бэке, без polling-а.
 */
function RealtimeConnector() {
  const dispatch = useDispatch();
  const token = useAppSelector((s) => s.auth.accessToken);
  useEffect(() => {
    if (!token) {
      realtime.disconnect();
      return;
    }
    realtime.connect(token);
    const unbind = installRealtimeBridge(dispatch);
    return () => {
      unbind();
    };
  }, [token, dispatch]);
  return null;
}

export function App() {
  return (
    <Provider store={store}>
      <ThemedApp />
    </Provider>
  );
}
